import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/**
 * Centralizes "the user's session has expired" UX. PI-1372.
 *
 * The `baseApi` baseQuery wrapper (see `baseApi.ts`) detects HTTP 401s on
 * auth-required Auto Order endpoints and dispatches `setSessionExpired`
 * with the path the user was trying to reach. A mounted `<SessionExpiryHandler />`
 * watches this slice and, when it flips on, fires the toast + router.push to
 * `/login?origin=...`.
 *
 * This replaces the page-local `useEffect(() => isUnauthorized(queryError) →
 * router.push(...))` pattern we'd been planting in every migrated page.
 *
 * Scope today: auto-order endpoints only (gated in `baseApi.ts`). If this
 * pattern proves valuable for the rest of the app, the gating list expands.
 */

export interface SessionExpiryState {
  expired: boolean;
  originPath: string | null;
}

const initialState: SessionExpiryState = {
  expired: false,
  originPath: null,
};

export const sessionExpirySlice = createSlice({
  name: "sessionExpirySlice",
  initialState,
  reducers: {
    setSessionExpired: (state, action: PayloadAction<{ originPath: string }>) => {
      // Idempotent: ignore duplicate fires while we're already redirecting.
      if (state.expired) return state;
      state.expired = true;
      state.originPath = action.payload.originPath;
    },
    clearSessionExpired: (state) => {
      state.expired = false;
      state.originPath = null;
    },
  },
});

export const { setSessionExpired, clearSessionExpired } = sessionExpirySlice.actions;
export default sessionExpirySlice;
